import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { defaultInvoiceTextTranslationKeys } from '@features/invoices/constants/defaultInvoiceTextTranslationKeys'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useDefaultTradeName } from '@features/invoicingSettings/hooks/useDefaultTradeName'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'
import { useZervantDocumentTemplateSettings } from '@features/invoicingSettings/hooks/useZervantDocumentTemplateSettings'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { useZervantInvoiceLogoUrl } from '@features/invoicingSettings/modules/logoAndBranding'
import {
  useGetAutomaticPaymentRemindersSettings,
  usePredefinedPaymentMethodsValues,
} from '@features/invoicingSettings/modules/paymentsAndReminders'
import { ZervantTemplateSettingsArea } from '@features/invoicingSettings/types/templates/zervantTemplateSettingsArea'
import { getDocumentDefaultsFormValues } from '@features/invoicingSettings/utils/getDocumentDefaultsFormValues'
import { useGetVatRate } from '@features/vatRates/hooks/useGetVatRate'

import { InvoiceSettingsForm } from '../types/invoiceSettingsForm'

export const usePredefinedInvoiceSettingsFormValues = () => {
  const { t } = useTranslation()
  const { data: zervantSettings, isLoading: isLoadingZervantSettings } = useZervantSettings()
  const { defaultTradeName, isLoading: isLoadingDefaultTradeName } = useDefaultTradeName()
  const { logoUrl, isLoading: isLoadingLogoUrl } = useZervantInvoiceLogoUrl()
  const { getVatRate } = useGetVatRate()

  const {
    getDefaultSettingsValue,
    getIsSettingsFieldVisible,
    isLoading: isLoadingZervantDocumentSettings,
  } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)

  const { data: documentTemplateSettings, isLoading: isLoadingZervantDocumentTemplateSettings } =
    useZervantDocumentTemplateSettings()

  const { isLoading: isLoadingPaymentMethods, predefinedPaymentMethodsValues } = usePredefinedPaymentMethodsValues()

  const { automaticPaymentRemindersSettings, isLoading: isLoadingPaymentReminderSettings } =
    useGetAutomaticPaymentRemindersSettings(t)

  const defaultVatRate = useMemo(
    () => getVatRate({ vatRateId: zervantSettings?.values.company.vatRateId }),
    [getVatRate, zervantSettings?.values.company.vatRateId],
  )

  const isLoading =
    isLoadingZervantDocumentSettings ||
    isLoadingZervantDocumentTemplateSettings ||
    isLoadingDefaultTradeName ||
    isLoadingLogoUrl ||
    isLoadingZervantSettings ||
    isLoadingPaymentMethods ||
    isLoadingPaymentReminderSettings

  const shouldApplyTemplateColor = useCallback(
    (area: ZervantTemplateSettingsArea) => {
      return documentTemplateSettings?.areas.includes(area)
    },
    [documentTemplateSettings?.areas],
  )

  const predefinedInvoiceSettingsFormValues: Partial<InvoiceSettingsForm> = useMemo(() => {
    const defaultContact = defaultTradeName?.contact
    const defaultLogoId = defaultTradeName?.logo?.id
    const defaultLogoUrl = defaultLogoId ? logoUrl : undefined
    const {
      currency,
      language: defaultLanguage,
      paymentTerm,
      productType,
    } = getDocumentDefaultsFormValues(getDefaultSettingsValue)
    const defaultFooterNote =
      getDefaultSettingsValue('footerText') ??
      t(defaultInvoiceTextTranslationKeys.footerNote, { lng: invoiceLanguageToLocale[defaultLanguage] })
    const defaultMessage =
      getDefaultSettingsValue('description') ??
      t(defaultInvoiceTextTranslationKeys.message, { lng: invoiceLanguageToLocale[defaultLanguage] })
    const legalMentions = zervantSettings?.values.company.legalMentions
    const legalFormSettings = zervantSettings?.values.company.legalFormSettings

    return {
      automaticPaymentRemindersSettings,
      contactEmail: defaultContact?.email,
      contactFirstName: defaultContact?.name?.first,
      contactLastName: defaultContact?.name?.last,
      contactPhone: defaultContact?.phone,
      currency,
      footerNote: defaultFooterNote,
      language: invoiceLanguageToLocale[defaultLanguage],
      legalFormSettings,
      legalMentions,
      logoId: defaultLogoId,
      logoUrl: defaultLogoUrl,
      message: defaultMessage,
      paymentMethods: predefinedPaymentMethodsValues,
      paymentTerm,
      productType,
      shouldApplyTemplateColorToColumnHeaders: shouldApplyTemplateColor('columnHeaders'),
      shouldApplyTemplateColorToDividers: shouldApplyTemplateColor('dividers'),
      shouldApplyTemplateColorToHeader: shouldApplyTemplateColor('header'),
      shouldApplyTemplateColorToTotal: shouldApplyTemplateColor('total'),
      shouldShowCustomerId: getIsSettingsFieldVisible('customerNumber'),
      shouldShowTermsAndConditions: getIsSettingsFieldVisible('legalterms'),
      templateColor: documentTemplateSettings?.color.text,
      templateFont: documentTemplateSettings?.font,
      termsAndConditions: getDefaultSettingsValue('legalterms'),
      vatRateId: defaultVatRate?.id,
      vatRatePercent: defaultVatRate?.rate,
    }
  }, [
    automaticPaymentRemindersSettings,
    defaultTradeName,
    defaultVatRate,
    documentTemplateSettings,
    getDefaultSettingsValue,
    getIsSettingsFieldVisible,
    logoUrl,
    predefinedPaymentMethodsValues,
    shouldApplyTemplateColor,
    zervantSettings?.values.company.legalMentions,
    zervantSettings?.values.company.legalFormSettings,
    t,
  ])

  return { isLoading, predefinedInvoiceSettingsFormValues }
}
